import { AfterViewInit, Component, forwardRef, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService }                    from '@ngx-translate/core';
import { UIRouter }                            from '@uirouter/core';
import { Subscription }                        from 'rxjs';

import { ApiService }                   from '../api/api.service';
import { DateParserService }            from '../core/services/date-parser.service';
import { EventBusService }              from '../core/services/event-bus.service';
import { BindHtmlComponent }            from '../table/cell-components/bind-html.component';
import { Table }                        from '../table/types/table';
import { TableService }                 from '../table/table.service';
import { TableQueryParameters }         from '../table/types/table-query-parameters';
import { TransactionCenterBookingModel } from './models/transaction-center.booking';
import { TransactionCenterFiltersModel } from './models/transaction-center.filters';

@Component({
  selector: 'wor-tc-bookings',
  template: require('./transaction-center.bookings.component.html')
})
export class TransactionCenterBookingsComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() bookings: Array<TransactionCenterBookingModel>;

  filters: TransactionCenterFiltersModel;
  tableSettings: Table;

  private tableCacheKey: string;
  private subscription: Subscription;

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => DateParserService))
    private dateParserService: DateParserService,
    @Inject(forwardRef(() => EventBusService))
    private eventBusService: EventBusService,
    @Inject(forwardRef(() => TableService)) private tableService: TableService,
    private translateService: TranslateService,
    private uiRouter: UIRouter
  ) {}

  ngOnInit(): void {
    this.tableCacheKey = this.uiRouter.stateService.current.data.tableCacheKey;

    this.filters = this.tableService.setFiltersFromCache(this.tableCacheKey, {
      endDate  : this.dateParserService.weekRangeEnd(null),
      range    : 'week',
      startDate: this.dateParserService.weekRangeStart(null),
      value    : ''
    });

    this.tableSettings = {
      broadcast: 'filterBookings',
      cache: { enabled: true, key: this.tableCacheKey },
      columns: [
        { field: 'event_date', header: { text: this.translateService.instant('JS_SPACE.COLUMNS.EVENT_DATE') }, pipe: 'globalDate' },
        { field: 'customer_display_name', header: { text: this.translateService.instant('CUSTOMER_SPACE.CUSTOMER') } },
        { field: 'customer_mobile', header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.MOBILE') }, pipe: 'mobilePhone' },
        { field: 'vehicle_make', header: { text: this.translateService.instant('VEHICLE_SPACE.MAKE') } },
        { field: 'vehicle_model', header: { text: this.translateService.instant('VEHICLE_SPACE.MODEL') } },
        { field: 'description', header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION') } },
        { classes: 'text-right', field: 'estimated_hours', header: { classes: 'text-right', text: this.translateService.instant('BOOKING_SPACE.ESTIMATED_HOURS') } },
        {
          classes: 'text-left',
          component: { bindings: { inputs: { model: 'event_status', pipe: 'eventStatus' } }, enabled: true, factory: BindHtmlComponent },
          header: { classes: 'text-left', text: this.translateService.instant('GENERAL_SPACE.FIELD.STATUS') },
          sortable: false
        }
      ],
      empty: { text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_BOOKINGS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
        pageSizes: [10, 25, 50],
        queryCount: () => this.getCount()
      },
      rows: { highlight: { enabled: true }, select: { enabled: true } },
      sorting: this.tableService.setSortingFromCache(this.tableCacheKey, { direction: 'desc', field: 'event_date' })
    };

    this.subscription = this.eventBusService.on<string>('searchFilterChanged').subscribe(value => {
      if (!this.uiRouter.stateService.transition) {
        this.filters.value = value;
        this.onFiltersChanged();
      }
    });
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      if (typeof window['filterBookings'] === 'function') {
        clearInterval(interval);
        this.onFiltersChanged();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onFiltersChanged(): void {
    if (window['filterBookings']) {
      window['filterBookings'](this.filters);
    }
  }

  get startDateDisplay(): string { return this.filters.startDate === '*' ? '' : this.filters.startDate; }
  set startDateDisplay(value: string) { this.filters.startDate = value || '*'; this.onFiltersChanged(); }

  get endDateDisplay(): string { return this.filters.endDate === '*' ? '' : this.filters.endDate; }
  set endDateDisplay(value: string) { this.filters.endDate = value || '*'; this.onFiltersChanged(); }

  getBookings(params: TableQueryParameters): Promise<Array<TransactionCenterBookingModel>> {
    return this.apiService.get('/transaction_center/bookings', [
      this.filters.startDate || this.dateParserService.weekRangeStart(null),
      this.filters.endDate   || this.dateParserService.weekRangeEnd(null),
      this.filters.value     || '*',
      params.field,
      params.direction,
      params.pageSize,
      params.offset
    ]).then((bookings: Array<TransactionCenterBookingModel>) => {
      this.bookings = bookings;
      return bookings;
    });
  }

  getCount(): Promise<number> {
    return this.apiService.get('/transaction_center/bookings_count', [
      this.filters.startDate || this.dateParserService.weekRangeStart(null),
      this.filters.endDate   || this.dateParserService.weekRangeEnd(null),
      this.filters.value     || '*'
    ]).then((count) => count.bookings_count);
  }

  isRange(range: string): boolean {
    return range === this.filters.range;
  }

  setDateRange(range: string): void {
    this.filters.range = range;

    switch (range) {
      case 'month':
        this.filters.endDate   = this.dateParserService.thisMonthEnd();
        this.filters.startDate = this.dateParserService.thisMonthStart();
        break;
      case 'today':
        this.filters.endDate   = this.dateParserService.today();
        this.filters.startDate = this.dateParserService.today();
        break;
      case 'week':
      default:
        this.filters.endDate   = this.dateParserService.weekRangeEnd(null);
        this.filters.startDate = this.dateParserService.weekRangeStart(null);
        break;
    }

    this.onFiltersChanged();
  }

  viewBooking(booking: TransactionCenterBookingModel): void {
    this.uiRouter.stateService.go('app.event', {
      customer_id: booking.customer_id,
      id         : booking.event_id,
      vehicle_id : booking.vehicle_id
    });
  }
}
